import React, { useCallback, useEffect, useRef, useState } from "react";
import { Chip, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../services/axiosInstance";
import apiEndPointsConfig from "../services/apiEndPoints";
import { useAttendanceEvents } from "../services/attendanceSocket";
import { breakLabel, formatClock } from "../utils/attendance";

// Live clock state in the agent app bar. It sits on every agent screen so an
// agent never has to open the attendance page to notice they are still on a
// break -- the commonest way break time gets over-reported.

const AGENT_PATH_PREFIX = "/agent";

export default function ClockStatus() {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(null);
  const [ready, setReady] = useState(false);
  const offsetRef = useRef(0);
  const [, setTick] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(apiEndPointsConfig.attendanceMe);
      if (data?.serverTime) {
        offsetRef.current = new Date(data.serverTime).getTime() - Date.now();
      }
      setCurrent(data?.current || null);
    } catch {
      // Not an agent token, or attendance is unavailable: show nothing rather
      // than an error badge on every screen.
      setCurrent(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    if (!window.location.pathname.startsWith(AGENT_PATH_PREFIX)) {
      setReady(true);
      return;
    }
    load();
  }, [load]);

  useAttendanceEvents("attendance.self", () => load(), load);

  const status = current?.status || "off";

  useEffect(() => {
    if (status === "off") return undefined;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [status]);

  if (!ready) return null;

  const now = Date.now() + offsetRef.current;
  const go = () => navigate("/agent/attendance");

  if (status === "off") {
    return (
      <Tooltip title="You are not clocked in - open attendance">
        <Chip
          label="Clock In"
          size="small"
          onClick={go}
          sx={{
            bgcolor: "rgba(255,255,255,0.15)",
            color: "white",
            fontWeight: 700,
            cursor: "pointer",
            "&:hover": { bgcolor: "rgba(255,255,255,0.28)" },
          }}
        />
      </Tooltip>
    );
  }

  const onBreak = status === "on_break";
  const since = onBreak
    ? new Date(current.currentBreak.startAt).getTime()
    : new Date(current.clockInAt).getTime();

  let elapsed = Math.max(0, now - since);
  if (!onBreak) {
    // Show productive time, not wall time on shift.
    let breakMs = 0;
    for (const b of current.breaks || []) {
      const end = b.endAt ? new Date(b.endAt).getTime() : now;
      breakMs += Math.max(0, end - new Date(b.startAt).getTime());
    }
    elapsed = Math.max(0, elapsed - breakMs);
  }

  return (
    <Tooltip
      title={
        onBreak
          ? `On ${breakLabel(current.currentBreak)} - open attendance to resume`
          : "Productive time this shift - open attendance"
      }
    >
      <Chip
        label={formatClock(elapsed)}
        size="small"
        onClick={go}
        sx={{
          bgcolor: onBreak ? "#d97706" : "#059669",
          color: "white",
          fontWeight: 700,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          cursor: "pointer",
          "&:hover": { opacity: 0.9 },
        }}
      />
    </Tooltip>
  );
}
